// Design values with no exact match in the theme or the design system.
// They are used exactly as the design specifies instead of the nearest
// available token / variant / component default. Every entry is listed by ID
// in .claude/projects/control-center/subscription/PRICING-BUILD-DECISIONS.md —
// correct a value there by ID and change it here.

export const PRICING_DESIGN_VALUES = {
  /** DV-1 · plan + AI price text 56/700/64 (h64 is 64/700/64) */
  priceText: { fontSize: "56px" },
  /** DV-2 · struck plan price 32/400/44 (no 32px regular variant) */
  originalPriceText: { fontSize: "32px", lineHeight: "44px" },
  /** DV-3 · plan name, AI card + AI detail titles 16/600/24 (h6 is 16/600/22) */
  titleText: { lineHeight: "24px" },
  /** DV-4 · AI metric values 24/700/32 (h4 is 24/700/28) */
  metricValueText: { lineHeight: "32px" },
  /** DV-5 · eyebrow letter-spacing 1px (caption-3 has 0.5px) */
  eyebrowText: { letterSpacing: "1px" },
  /** DV-6 · feature heading has no letter-spacing (caption-2 has 0.5px) */
  featuresHeadingText: { letterSpacing: "0px" },
  /** DV-7 · "SUBSCRIPTION PLANS" eyebrow gradient text (spec) — no gradient tokens */
  plansEyebrowGradient: "linear-gradient(115deg, #0ab8cd, #7335e5)",
  /** PH-9 · "OPTIONAL ADD-ON" eyebrow gradient — not in the spec; sampled from design-screenshots/7-.png */
  aiEyebrowGradient: "linear-gradient(115deg, #602aee, #e94bd4)",
  /** DV-8 · background glows — colours not in the theme */
  glow: {
    top: "64px",
    height: "538px",
    size: "537.6px",
    // The spec's blur(268.8px) is Figma's layer-blur value copied into CSS;
    // CSS blur() needs half of it to match the design render (measured).
    blur: "134.4px",
    leftOffset: "-44px",
    leftColor: "#fff7ef",
    rightOffset: "650px",
    rightColor: "#f0edfe",
  },
  /** DV-9 · AI price card gradient border (7 stops, 110deg) */
  aiCardBorderGradient:
    "linear-gradient(110deg, #ed25c1 0%, #f096ff 33.33%, #ecb4e0 39.02%, #ff96e2 46.41%, #a74fff 75.97%, #874fff 84.88%, #4d25ed 100%)",
  /** DV-10 · pricing buttons: padding 8px 12px, 12/600/16 (Button small is 0 16 0 12, line-height 24) */
  buttonOverrides: { padding: "8px 12px", lineHeight: "16px" },
  /** DV-11 · glass effect from the spec */
  planCardBackdrop: "blur(4px)",
  popularBannerBackdrop: "blur(8px)",
  /** DV-12 · shadows — no shadow tokens */
  shadows: {
    eyebrow: "2px 2px 8px rgb(0 0 0 / 8%)",
    planSummary: "3px 6px 8px rgb(0 0 0 / 4%)",
    aiCard: "0 0 4px rgb(0 0 0 / 16%), 0 0 2px rgb(0 0 0 / 8%)",
  },
  /**
   * DV-13 · MOST POPULAR pill glass shading (component CSS spec from Figma).
   * The outer shadow is exact (Figma properties: 0 4 8 0 #000 4%); the two
   * inset shadows and the top gradient approximate Figma's Glass effect,
   * which has no CSS equivalent. Lightened on request (spec had 12% / 5%);
   * 6% / 3.5% is the closest rendered match to the Figma screenshot.
   */
  popularBannerGlass: {
    boxShadow:
      "0 4px 8px 0 rgb(0 0 0 / 4%), inset 0 1px 1px rgb(255 255 255 / 80%), inset 0 3px 5px -3px rgb(0 0 0 / 6%)",
    topShade:
      "linear-gradient(to bottom, rgb(0 0 0 / 3.5%) 0%, rgb(0 0 0 / 1.5%) 18%, transparent 45%)",
  },
  /** PH-5 · scroll-down button offset — measured from the hero screenshot */
  scrollButton: { right: "24px", bottom: "32px" },
  /**
   * LD-6 · AI struck price ($399) at the top-right of $199. The spec puts it
   * in the meta column; the design shows it as a superscript. Offsets tuned by
   * rendering with the app's Segoe fonts against design-screenshots/7-.png.
   */
  aiOriginalPricePosition: { top: "5px", left: "calc(100% - 3px)" },
  /**
   * DV-15 · cancelled banner "Contact sales": the spec link is 12/600/16.
   * Button ghost isLink is 32px min-height / 24px line-height, which would
   * make the banner 56px instead of 44px.
   */
  bannerLinkButton: { minHeight: "16px", lineHeight: "16px" },
  /**
   * LD-4 (corrected) · the page is a white panel inside a grey frame.
   * gap: 8px (screenshots; confirmed by the user). radius: 8px (css-spec
   * .pricing-page). The frame colour (PH-10) is set in PricingPageFrame.tsx.
   */
  pageFrame: { gap: "8px", radius: "8px" },
} as const;
